import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, InputLabel, Radio,
    RadioGroup, Table, TableBody, TableCell, TableHead, TableRow,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { coresDoTema } from "assets/coresTema";
import { errorAlert, infoAviso, successAlert } from "utils/Functions";
import { get, post } from "utils/WebService";

const SEM_LISTA = "Todos os clientes (sem lista)";
const listaDeClientes = (clientes) => (clientes && clientes.length ? clientes.join(", ") : SEM_LISTA);
const escapar = (t) => String(t ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/**
 * Replicar os clientes liberados (campo Empresa do usuário) de uma pessoa da equipe para as outras (Usuários > Editar):
 * escolhe o modelo, quem recebe (só as pessoas ativas da equipe) e o modo (substituir a lista ou acrescentar os clientes do modelo). "Ver o que muda" simula
 * no backend (/equipe/{id}/replicar-clientes com simular) e só então "Replicar" grava, com o histórico de cada usuário.
 * Modelo sem lista + substituir = limpa a lista de quem recebe (vê todos; em equipe, os Global e os das carteiras dela).
 */
const ReplicarClientesDialog = ({ equipe, onClose }) => {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const cores = coresDoTema(darkMode);
    const [pessoas, setPessoas] = useState(null);
    const [modelo, setModelo] = useState(null);
    const [destinos, setDestinos] = useState([]);
    const [modo, setModo] = useState("SUBSTITUIR");
    const [previa, setPrevia] = useState(null);
    const [ocupado, setOcupado] = useState(false);
    // Na equipe grande a prévia fica no fim da lista: ao chegar, rola até ela (senão o botão parecia não fazer nada)
    const refPrevia = useRef(null);

    useEffect(() => {
        get(`/equipe/${equipe.id}/clientes-usuarios`)
            .then((r) => setPessoas(r.pessoas || []))
            .catch((err) => { errorAlert(escapar(err?.message) || "Erro ao carregar as pessoas da equipe."); onClose(); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [equipe.id]);

    // Qualquer mudança na escolha invalida a prévia
    useEffect(() => setPrevia(null), [modelo, destinos, modo]);

    useEffect(() => {
        if (previa && refPrevia.current) refPrevia.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [previa]);

    const escolherModelo = (p) => {
        setModelo(p);
        // Quem recebe: as pessoas da equipe (o backend só manda as ativas), menos o modelo
        setDestinos((pessoas || []).filter(x => x.id !== p?.id).map(x => x.id));
    };

    const alternar = (id) => setDestinos(d => (d.includes(id) ? d.filter(x => x !== id) : [...d, id]));

    const enviar = (simular) => post(`/equipe/${equipe.id}/replicar-clientes`, { modelo: modelo.id, destinos, modo, simular });

    const verPrevia = () => {
        setOcupado("simular");
        enviar(true)
            .then(setPrevia)
            .catch((err) => errorAlert(escapar(err?.message) || "Erro ao simular."))
            .finally(() => setOcupado(false));
    };

    const replicar = () => {
        infoAviso(`Gravar os clientes liberados de <b>${previa.alterados}</b> usuário(s) da equipe <b>${escapar(equipe.nome)}</b>? `
            + "Cada troca vai para o histórico do usuário.", () => {
            setOcupado("replicar");
            enviar(false)
                .then((r) => { successAlert(`${r.alterados} usuário(s) alterado(s).`); onClose(); })
                .catch((err) => errorAlert(escapar(err?.message) || "Erro ao replicar os clientes."))
                .finally(() => setOcupado(false));
        });
    };

    const celula = { fontSize: 12, color: cores.texto, verticalAlign: "top", pt: 1.5 };
    const cabecalho = { fontSize: 12, fontWeight: "bold", color: cores.texto, backgroundColor: cores.painel };
    const outros = (pessoas || []).filter(p => p.id !== modelo?.id);

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Replicar clientes liberados · {equipe.nome}</DialogTitle>
            <DialogContent>
                {!pessoas ? (
                    <MDBox display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></MDBox>
                ) : (
                    <>
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Usuário modelo</InputLabel>
                                <MDSelect
                                    getOptionValue={o => o.id}
                                    getOptionLabel={o => o.nome}
                                    placeholder="Quem já está configurado"
                                    value={modelo}
                                    options={pessoas}
                                    onChange={escolherModelo}
                                />
                                {modelo && (
                                    <MDTypography variant="caption" color="text" display="block" sx={{ px: 1 }}>
                                        Clientes do modelo: {listaDeClientes(modelo.clientes)}
                                    </MDTypography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Modo</InputLabel>
                                <RadioGroup value={modo} onChange={(e) => setModo(e.target.value)} sx={{ px: 1 }}>
                                    <FormControlLabel value="SUBSTITUIR" control={<Radio size="small" />}
                                        label={<MDTypography variant="button" sx={{ color: cores.texto }}>Substituir a lista de cada um pela do modelo</MDTypography>} />
                                    <FormControlLabel value="ACRESCENTAR" control={<Radio size="small" />}
                                        label={<MDTypography variant="button" sx={{ color: cores.texto }}>Acrescentar os clientes do modelo</MDTypography>} />
                                </RadioGroup>
                                <MDTypography variant="caption" color="text" display="block" sx={{ px: 1 }}>
                                    Modelo sem lista + substituir = limpa a lista (vê todos; quem está em equipe, os Global e os das carteiras dela).
                                </MDTypography>
                            </Grid>
                        </Grid>

                        {modelo && (
                            <>
                                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ mt: 2, mb: 0.5, color: cores.texto }}>
                                    Quem recebe ({destinos.length} de {outros.length})
                                </MDTypography>
                                <Table size="small">
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow>
                                            {["", "Pessoa", "Papel na equipe", "Clientes liberados hoje"].map((t) => (
                                                <TableCell key={t} sx={cabecalho}>{t}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {outros.map((p) => (
                                            <TableRow key={p.id}>
                                                <TableCell sx={{ ...celula, pt: 0.5, width: 40 }}>
                                                    <Checkbox size="small" checked={destinos.includes(p.id)} onChange={() => alternar(p.id)} />
                                                </TableCell>
                                                <TableCell sx={celula}>{p.nome}</TableCell>
                                                <TableCell sx={celula}>{(p.papeis || []).join(", ")}</TableCell>
                                                <TableCell sx={celula}>{listaDeClientes(p.clientes)}</TableCell>
                                            </TableRow>
                                        ))}
                                        {!outros.length && (
                                            <TableRow><TableCell colSpan={4} sx={celula}>A equipe não tem outras pessoas ativas.</TableCell></TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </>
                        )}

                        {previa && (
                            <MDBox ref={refPrevia} sx={{ scrollMarginTop: 8 }}>
                                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ mt: 2, mb: 0.5, color: cores.texto }}>
                                    O que muda: {previa.alterados} de {previa.resultado.length} usuário(s)
                                </MDTypography>
                                {previa.alterados > 0 && (
                                    <Table size="small">
                                        <TableHead sx={{ display: "table-header-group" }}>
                                            <TableRow>
                                                {["Usuário", "Antes", "Depois"].map((t) => <TableCell key={t} sx={cabecalho}>{t}</TableCell>)}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {previa.resultado.filter(r => r.muda).map((r) => (
                                                <TableRow key={r.usuario}>
                                                    <TableCell sx={celula}>{r.usuario}</TableCell>
                                                    <TableCell sx={{ ...celula, color: cores.textoSecundario }}>{r.antes}</TableCell>
                                                    <TableCell sx={{ ...celula, fontWeight: "bold" }}>{r.depois}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                                {previa.resultado.some(r => !r.muda) && (
                                    <MDTypography variant="caption" color="text" display="block" sx={{ mt: 1 }}>
                                        Sem mudança: {previa.resultado.filter(r => !r.muda).map(r => r.usuario).join(", ")}.
                                    </MDTypography>
                                )}
                            </MDBox>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                {previa && (
                    <MDTypography variant="caption" sx={{ mr: "auto", pl: 2, color: cores.texto }}>
                        {previa.alterados ? `Prévia: ${previa.alterados} de ${previa.resultado.length} usuário(s) mudam.` : "Prévia: ninguém muda."}
                    </MDTypography>
                )}
                <MDButton variant="text" color="secondary" onClick={onClose}>Fechar</MDButton>
                <MDButton variant="outlined" color="info" disabled={!modelo || !destinos.length || !!ocupado} onClick={verPrevia}
                    endIcon={ocupado === "simular" ? <CircularProgress color="inherit" size={16} /> : null}>
                    {ocupado === "simular" ? "Simulando..." : "Ver o que muda"}
                </MDButton>
                <MDButton variant="contained" color="info" disabled={!previa || !previa.alterados || !!ocupado} onClick={replicar}
                    endIcon={ocupado === "replicar" ? <CircularProgress color="inherit" size={16} /> : null}>
                    Replicar
                </MDButton>
            </DialogActions>
        </Dialog>
    );
};

ReplicarClientesDialog.propTypes = {
    equipe: PropTypes.shape({ id: PropTypes.string.isRequired, nome: PropTypes.string }).isRequired,
    onClose: PropTypes.func.isRequired,
};

export default ReplicarClientesDialog;
